<script setup lang="ts">
import { ref, computed, onBeforeMount, onMounted, onUnmounted } from 'vue';
import { useUxData } from "@/stores/ux-data";
import { useAppDataStore } from "@/stores/app-data";
import { usePredicateStore } from "@/stores/predicate-store";
import { handleError } from "@/appdata/globals";
import { useSearchableList } from "@/composables/useSearchableList";
import { useDoubleClick } from "@/composables/useDoubleClick";
import ElementGrid from "@/components/data/grid/ElementGrid.vue";
import FilterControl from "@/components/atoms/element-filters/FilterControl.vue";
import WorldFinder from "@/components/atoms/WorldFinder.vue";
import type { ColumnConfig, FilterChip } from "@/types/browser";
import type { SettingElementInterface } from "@/types/items";
import type { SettingRuleInterface, WorldInterface } from "@/types/worlds";

// The setting-rule list, shared by world creation (WorldRulesStep) and the character-local
// setting configurator. The boundary is deliberately narrow: this owns the list, the
// source-filter scope and which uuids are ticked — the host owns what "chosen" means.
//
// Access scoping needs no work here: the endpoint and systemModPredicates already return only
// rules the user can see.
const props = withDefaults(defineProps<{
    selected: string[],
    seedSources?: WorldInterface[],
}>(), { seedSources: () => [] });

const emit = defineEmits<{ (e: 'toggle', rule: SettingRuleInterface): void }>();

const ux = useUxData();
const store = useAppDataStore();
const predicateStore = usePredicateStore();

const selectedRule = ref<SettingRuleInterface | null>(null);

// A search and the source chips each ask again, and the answers can land in any order, so the
// list is useSearchableList's: only the latest request fills it, `loading` stays on until that
// one lands — which is what the grid's spinner and its pager go by — and the search waits for
// the user to stop typing, since the grid reports every keyup.
const { dataItems: rules, loading, metadata, links, fetch: fetchRules, onNextPage, onPreviousPage, onSearch: onKeywordChange } =
    useSearchableList<SettingRuleInterface>({
        keyword: computed({
            get: () => predicateStore.systemModFilters.stringPartial,
            set: (value) => { predicateStore.systemModFilters.stringPartial = value; },
        }),
        fetchPage: (url) => store.fetchSettingRules(url, predicateStore.systemModPredicates)
            .catch((error) => { handleError(error); }),
        // A changed text is a new list: the rule picked from the old one no longer counts.
        onTyped: () => { selectedRule.value = null; },
    });

const isRuleAdded = (rule: SettingRuleInterface): boolean =>
    props.selected.includes(rule.uuid ?? '');

const columns: ColumnConfig[] = [
    { field: 'name', header: ux.labels?.swade?.data?.settingRules?.name ?? 'Name' },
    {
        field: 'source.name',
        header: ux.labels?.swade?.data?.settingRules?.source ?? 'Source',
        formatter: (el: any) => el.source?.name ?? el.worlds?.at(0)?.name ?? '—',
    },
];

const SOURCE_CHIP_PREFIX = 'source:';

// Chips reflect the active source-filter SCOPE, not the host's selection. Removing one narrows
// the rules view only — it never mutates what the host has chosen.
const chips = computed<FilterChip[]>(() => {
    const sourceLabel = ux.labels?.swade?.data?.settingRules?.source ?? 'Source';
    return predicateStore.sources.map((w) => ({
        key: `${SOURCE_CHIP_PREFIX}${w.uuid}`,
        label: `${sourceLabel}: ${w.name}`,
    }));
});

// Mirror ElementBrowser: drive double-click off the grid's single `select` event rather than a
// native @dblclick, which is unreliable when a click triggers the mobile details offcanvas.
// The grid hands back its shared element type. Every row in this grid is a setting rule, so the
// row is re-typed where it is used: an assertion, not a conversion.
const { onClick: onRowSelect } = useDoubleClick(
    (rule: SettingElementInterface) => {
        selectedRule.value = rule as SettingRuleInterface;
        emit('toggle', rule as SettingRuleInterface);
    },
    { onSingleClick: (rule: SettingElementInterface) => { selectedRule.value = rule as SettingRuleInterface; } },
);

const onWorldChange = (event: any) => {
    if (!event.item) return;
    if (!predicateStore.sources.some((w) => w.uuid === event.item.uuid)) {
        predicateStore.sources = [...predicateStore.sources, event.item];
    }
    fetchRules();
};

const onRemoveChip = (chip: FilterChip) => {
    if (!chip.key.startsWith(SOURCE_CHIP_PREFIX)) return;
    const uuid = chip.key.slice(SOURCE_CHIP_PREFIX.length);
    predicateStore.sources = predicateStore.sources.filter((w) => w.uuid !== uuid);
    fetchRules();
};

// The grid empties its own search box on Clear and leaves the keyword behind it to the host.
const onClearFilters = () => {
    predicateStore.sources = [];
    predicateStore.systemModFilters.stringPartial = "";
    fetchRules();
};

onBeforeMount(() => {
    if (store.system && store.system.uuid !== predicateStore.system?.uuid) {
        predicateStore.system = store.system;
    }
});

onMounted(() => {
    // The host decides the starting scope. Re-seeding on mount means ad-hoc additions reset when
    // the user leaves and returns.
    predicateStore.sources = [...props.seedSources];
    // The search box starts empty, so the keyword does too, whatever was left in the slot.
    predicateStore.systemModFilters.stringPartial = "";
    fetchRules();
});

// predicateStore.sources is a shared slot — clear it or the scope leaks to the next surface.
onUnmounted(() => {
    predicateStore.sources = [];
    predicateStore.systemModFilters.stringPartial = "";
});

</script>

<template>
  <ElementGrid
    class="setting-rule-picker"
    :data-items="rules as any"
    :columns="columns"
    :selected-element="selectedRule as any"
    :loading="loading"
    :metadata="metadata"
    :links="links"
    :filters-active="chips.length > 0"
    :show-details-panel="true"
    :search-placeholder="ux.labels?.placeholders?.search ?? 'Search...'"
    :empty-message="ux.labels?.swade?.data?.settingRules?.empty ?? { message: 'No setting rules found.' }"
    @select="onRowSelect"
    @search="onKeywordChange"
    @next="onNextPage"
    @previous="onPreviousPage"
    @clear="onClearFilters"
  >
    <template #cell-name="{ element }">
      <span v-if="isRuleAdded(element as any)" class="d-lg-inline d-none fa-light fa-check text-success me-1"></span>
      {{ (element as any).name }}
      <span v-if="isRuleAdded(element as any)" class="d-lg-none fa-light fa-check text-success me-1"></span>
    </template>

    <template #details="{ element }">
      <p v-if="(element as any)?.summary" class="card-text" v-html="(element as any).summary"></p>
      <button
        v-if="element && !isRuleAdded(element as any)"
        type="button"
        class="btn btn-primary btn-sm use-setting-rule"
        @click="emit('toggle', element as any)"
        v-html="ux.labels?.guidedSetup?.world?.useSettingRule ?? 'Use this setting rule'"
      ></button>
      <button
        v-else-if="element"
        type="button"
        class="btn btn-outline-danger btn-sm remove-setting-rule"
        @click="emit('toggle', element as any)"
        v-html="ux.labels?.guidedSetup?.world?.removeSettingRule ?? 'Remove setting rule'"
      ></button>
    </template>

    <template #filters>
      <FilterControl
        :label="ux.labels?.swade?.data?.settingRules?.source ?? 'Source'"
        :chips="chips"
        :multiple="true"
        @remove="onRemoveChip"
      >
        <div class="form-group mb-3 mb-lg-0">
          <label class="fw-semibold d-lg-none d-block mb-1">{{ ux.labels?.filters?.source?.chip ?? 'World' }}</label>
          <!-- filters.source, NOT filters.world: this finder sits inside a "Source"
               FilterControl and appends to predicateStore.sources. The source-picking siblings
               (GearList, AncestryList, CharacterGearStep) all use this key WITH the ?? fallback;
               the filters.world key belongs to the character-LIST world filter. Verbatim
               carry-over from WorldRulesStep — do not "modernise" it. -->
          <WorldFinder
            :include-all="true"
            :placeholder="ux.labels?.filters?.source?.placeholder ?? 'World'"
            @world-selected="onWorldChange"
          />
        </div>
      </FilterControl>
    </template>
  </ElementGrid>
</template>
